import type { ShippingAllocationMethod } from "@/server/platform/db/schema/inventory";
import type { LandedCostLinePreview, LandedCostPreview } from "../types";

export type AllocationLineInput = {
  variantId: string;
  productName?: string;
  variantName?: string;
  quantity: number;
  unitPurchaseCost: number;
  shippingAllocated?: number;
};

export type AllocationInput = {
  shippingFeeTotal: number;
  otherFeesTotal: number;
  shippingAllocationMethod: ShippingAllocationMethod;
  lines: AllocationLineInput[];
};

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * ຄຳນວນ landed cost ຕໍ່ບັນທັດ ໂດຍແບ່ງຄ່າຂົນສົ່ງ + ຄ່າອື່ນ ຕາມ method ທີ່ເລືອກ
 *
 * - by_value: ແບ່ງຕາມມູນຄ່າຕົ້ນທຸນສິນຄ້າຕໍ່ບັນທັດ
 * - by_quantity: ແບ່ງຕາມຈຳນວນ
 * - manual: ໃຊ້ shippingAllocated ທີ່ຜູ້ໃຊ້ໃສ່ (ຄ່າອື່ນແບ່ງ by_value)
 *
 * ປັດເສດການປັດເສດ (rounding) — ບັນທັດສຸດທ້າຍຮັບສ່ວນຕ່າງ ເພື່ອໃຫ້ຜົນລວມ = ຄ່າຂົນສົ່ງລວມ
 */
export function calculateLandedCosts(
  input: AllocationInput,
): LandedCostPreview {
  const shippingFee = Math.max(0, input.shippingFeeTotal || 0);
  const otherFees = Math.max(0, input.otherFeesTotal || 0);
  const extraToAllocate = shippingFee + otherFees;

  const lines = input.lines.map((line) => ({
    ...line,
    quantity: Math.max(0, Math.trunc(line.quantity || 0)),
    unitPurchaseCost: Math.max(0, line.unitPurchaseCost || 0),
  }));

  const purchaseSubtotal = round2(
    lines.reduce((sum, l) => sum + l.unitPurchaseCost * l.quantity, 0),
  );
  const totalQuantity = lines.reduce((sum, l) => sum + l.quantity, 0);

  let warning: string | undefined;

  // คำนวณสัดส่วนการแบ่งต่อบรรทัด (เป็นค่าดิบก่อน ปัดเศษทีหลัง)
  const rawShares: number[] = lines.map((line) => {
    if (input.shippingAllocationMethod === "manual") {
      return Math.max(0, line.shippingAllocated ?? 0);
    }
    if (input.shippingAllocationMethod === "by_quantity") {
      if (totalQuantity === 0) return 0;
      return extraToAllocate * (line.quantity / totalQuantity);
    }
    // by_value (default)
    const lineValue = line.unitPurchaseCost * line.quantity;
    if (purchaseSubtotal === 0) {
      // ถ้าไม่มีมูลค่าสินค้า ตกลงแบ่งตามจำนวนแทน
      if (totalQuantity === 0) return 0;
      return extraToAllocate * (line.quantity / totalQuantity);
    }
    return extraToAllocate * (lineValue / purchaseSubtotal);
  });

  if (input.shippingAllocationMethod === "manual") {
    const manualSum = round2(rawShares.reduce((s, v) => s + v, 0));
    if (manualSum !== round2(extraToAllocate)) {
      warning = `ຜົນລວມຄ່າຂົນສົ່ງທີ່ແບ່ງເອງ (${manualSum}) ບໍ່ກົງກັບຄ່າຂົນສົ່ງ+ຄ່າອື່ນລວມ (${round2(
        extraToAllocate,
      )})`;
    }
  }

  // ปัดเศษ + ปรับบรรทัดสุดท้ายให้ผลรวมตรง (เฉพาะ by_value / by_quantity)
  const rounded = rawShares.map((v) => round2(v));
  if (input.shippingAllocationMethod !== "manual" && lines.length > 0) {
    const roundedSum = round2(rounded.reduce((s, v) => s + v, 0));
    const diff = round2(extraToAllocate - roundedSum);
    if (diff !== 0) {
      const lastIdx = rounded.length - 1;
      rounded[lastIdx] = round2((rounded[lastIdx] ?? 0) + diff);
    }
  }

  const previewLines: LandedCostLinePreview[] = lines.map((line, i) => {
    const linePurchaseTotal = round2(line.unitPurchaseCost * line.quantity);
    const shippingAllocated = rounded[i] ?? 0;
    const lineLandedTotal = round2(linePurchaseTotal + shippingAllocated);
    const unitLandedCost =
      line.quantity > 0 ? round2(lineLandedTotal / line.quantity) : 0;
    return {
      variantId: line.variantId,
      productName: line.productName,
      variantName: line.variantName,
      quantity: line.quantity,
      unitPurchaseCost: round2(line.unitPurchaseCost),
      linePurchaseTotal,
      shippingAllocated,
      lineLandedTotal,
      unitLandedCost,
    };
  });

  const landedSubtotal = round2(
    previewLines.reduce((sum, l) => sum + l.lineLandedTotal, 0),
  );

  return {
    shippingFeeTotal: round2(shippingFee),
    otherFeesTotal: round2(otherFees),
    shippingAllocationMethod: input.shippingAllocationMethod,
    purchaseSubtotal,
    landedSubtotal,
    totalQuantity,
    lines: previewLines,
    warning,
  };
}
